/**
 * TrackingLookupForm — single-input form. Validates UPU S10 client-side before
 * navigation so the result page never mounts with an obviously invalid number.
 *
 * Submit button disabled until the input passes structural + checksum
 * validation. Error message shown below the input only AFTER the user has
 * tried to submit (or blurred the field) so they don't see "invalid" while
 * still typing.
 *
 * On submit: navigates to `/track/:trackingNumber`. The result page owns the
 * actual API call.
 */
import { useState, type FormEvent, type JSX, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { isValidTrackingNumber, normaliseTrackingNumber } from './upu-s10';

export function TrackingLookupForm(): JSX.Element {
  const navigate = useNavigate();
  const [raw, setRaw] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);

  const normalised = normaliseTrackingNumber(raw);
  const isValid = isValidTrackingNumber(normalised);
  const showError = touched && raw.length > 0 && !isValid;

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    setRaw(e.target.value);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    navigate(`/track/${normalised}`);
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-labelledby="lookup-heading" className="w-full">
      <h2 id="lookup-heading" className="sr-only">
        Tracking number lookup
      </h2>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex-1">
          <span className="sr-only">Tracking number</span>
          <input
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. EE123456785NG"
            value={raw}
            onChange={handleChange}
            onBlur={() => setTouched(true)}
            aria-invalid={showError}
            aria-describedby={showError ? 'tracking-error' : undefined}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-base font-mono uppercase tracking-wide text-slate-900 shadow-sm placeholder:font-sans placeholder:normal-case placeholder:text-slate-400 focus:border-nipost-600 focus:outline-none focus:ring-2 focus:ring-nipost-600/30"
          />
        </label>
        <button
          type="submit"
          disabled={!isValid}
          className="inline-flex items-center justify-center rounded-md bg-nipost-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-nipost-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Track
        </button>
      </div>
      {showError ? (
        <p id="tracking-error" role="alert" className="mt-2 text-sm text-rose-700">
          That doesn&apos;t look like a valid NIPOST tracking number. Format is 13 characters,
          e.g. <span className="font-mono">EE123456785NG</span>.
        </p>
      ) : null}
    </form>
  );
}
